// CSV取込画面：ヤマト・佐川CSVをアップロードし、自動判定・行数・突合サマリーを表示

import React, { useRef, useState } from "react";
import { Ic } from "../components/Icons";
import { useStore } from "../store";
import { readCsvFile } from "../lib/csv/parse";
import { parseSagawa } from "../lib/csv/sagawa";
import { parseYamato } from "../lib/csv/yamato";
import { detectSagawaUnknown, detectYamatoUnknownColumns, hasUnknown } from "../lib/csv/validate";
import type { UnknownReport } from "../lib/csv/validate";
import { detectPeriodMismatch, formatMonthLabel } from "../lib/csv/periodCheck";
import { isBlockedWhileLocked } from "../store";
import { yen } from "../lib/normalize";
import type { Carrier } from "../types";

const CARRIER_LABEL: Record<Carrier, string> = { yamato: "ヤマト運輸", sagawa: "佐川急便" };
const CARRIER_COLOR: Record<Carrier, string> = { yamato: "#0066b3", sagawa: "#0073c4" };

export function CsvImportScreen({ onNav }: { onNav: (s: string) => void }) {
  const { state, dispatch, refreshMaster, monthLocked } = useStore();
  const [error, setError] = useState<string | null>(null);
  const [blocked, setBlocked] = useState<UnknownReport | null>(null);
  const [busy, setBusy] = useState(false);

  const handleFile = async (file: File) => {
    setError(null);
    setBlocked(null);
    setBusy(true);
    try {
      const { encoding, carrier, rows } = await readCsvFile(file);
      if (carrier === "unknown") {
        throw new Error("ヤマト/佐川いずれのCSVヘッダーにも該当しませんでした");
      }
      const result =
        carrier === "sagawa"
          ? parseSagawa(rows, file.name, file.size, encoding)
          : parseYamato(rows, file.name, file.size, encoding);

      // Issue #7：未知の便種/列を検出したら、黙って通さず取込をブロックする
      const report: UnknownReport =
        "sagawaRows" in result
          ? { carrier: "sagawa", fileName: file.name, sagawa: detectSagawaUnknown(result.sagawaRows, state.sagawaBenkindRules) }
          : { carrier: "yamato", fileName: file.name, yamato: detectYamatoUnknownColumns(rows[0] ?? []) };
      if (hasUnknown(report)) {
        setBlocked(report);
        return; // ADD_IMPORT しない（計上漏れを防ぐためファイル全体を保留）
      }

      // 確定した月は取り込み直せない（本城さん要望 2026-09-01）。
      // reducer 側でも止めているが、黙って無視されると原因が分からないのでここで理由を出す。
      // 判定は reducer と同じ関数を使う（別々に書くとズレて「押しても何も起きない」になる）。
      const impMonth = result.meta.periodStart ? result.meta.periodStart.substring(0, 7) : null;
      if (isBlockedWhileLocked(state, { type: "ADD_IMPORT", imp: { meta: result.meta } })) {
        throw new Error(
          impMonth
            ? `${formatMonthLabel(impMonth)} は確定済みのため、このCSVは取り込めません。` +
              `${formatMonthLabel(impMonth)} を直す場合は「支払明細 一覧」でその月を開き、「この月を訂正する」を押してから取り込み直してください。`
            : `${formatMonthLabel(state.targetMonth)} は確定済みのため、対象期間を判定できないCSVは取り込めません。` +
              `訂正する場合は「支払明細 一覧」で「この月を訂正する」を押してください。`
        );
      }

      // 取込前にマスターシートの最新を取得（月別控除入力のガソリン等を古い写しで計算しないため。
      // 「シートに入力→CSV取込」の順で反映されない問題への恒久対応。失敗時は前回取得分で続行＝取込は止めない）
      await refreshMaster();

      dispatch({
        type: "ADD_IMPORT",
        imp: {
          meta: result.meta,
          sagawaRows: "sagawaRows" in result ? result.sagawaRows : undefined,
          yamatoRows: "yamatoRows" in result ? result.yamatoRows : undefined,
        },
      });
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const yamatoImp = state.imports.yamato;
  const sagawaImp = state.imports.sagawa;
  const totalRows = (yamatoImp?.meta.rows || 0) + (sagawaImp?.meta.rows || 0);
  const matchedCount = state.statements.length;
  const allCsvNames = new Set<string>();
  if (yamatoImp?.yamatoRows) yamatoImp.yamatoRows.forEach((r) => allCsvNames.add(r.driverName));
  if (sagawaImp?.sagawaRows) sagawaImp.sagawaRows.forEach((r) => allCsvNames.add(r.driverName));
  const unmatched =
    (state.matches.yamato?.filter((m) => m.status === "unmatched").length || 0) +
    (state.matches.sagawa?.filter((m) => m.status === "unmatched").length || 0);
  const candidates =
    (state.matches.yamato?.filter((m) => m.status === "candidate").length || 0) +
    (state.matches.sagawa?.filter((m) => m.status === "candidate").length || 0);

  // 月別控除入力シートの読込状況（当月分。0件だとシート未入力 or 月ズレの可能性）
  const monthlyDed = (state.sheetMaster?.monthlyDeductions || []).filter((d) => d.month === state.targetMonth);
  const gasDedCount = monthlyDed.filter((d) => d.item.includes("ガソリン")).length;

  // キャリア別売上集計
  const yamatoStmts = state.statements.filter((s) => s.carrier === "yamato");
  const sagawaStmts = state.statements.filter((s) => s.carrier === "sagawa");
  const yamatoSales = yamatoStmts.reduce((a, s) => a + s.salesTotal, 0);
  const sagawaSales = sagawaStmts.reduce((a, s) => a + s.salesTotal, 0);
  const yamatoZeroAlert = !!yamatoImp && yamatoStmts.length > 0 && yamatoSales === 0;
  const sagawaZeroAlert = !!sagawaImp && sagawaStmts.length > 0 && sagawaSales === 0;

  // 対象期間の月ズレ（本城さんの質問 2026-09-01）：
  // 片方のキャリアだけ差し替えると前月が残り、2か月分が混ざった明細が無警告で出来上がる。
  const periodMismatch = detectPeriodMismatch(yamatoImp?.meta, sagawaImp?.meta);

  // 取込済みCSVを外す。確定した月では外せない（理由を出して止める）
  const clearImport = (carrier: Carrier) => {
    if (monthLocked) {
      setError(
        `${state.targetMonth} は確定済みのため、取り込んだCSVは外せません。` +
        `次の月のCSVをアップロードすると、新しい月の作業に切り替わります（同じ業者のCSVは上書きされます）。` +
        `この月を直す場合は「支払明細 一覧」で「この月を訂正する」を押してください。`
      );
      return;
    }
    setError(null);
    dispatch({ type: "REMOVE_IMPORT", carrier });
  };

  return (
    <div className="content" style={{ maxWidth: 1280 }}>
      <div className="page-title-row">
        <div className="col gap-4">
          <div className="page-title">CSV取込</div>
          <div className="page-sub muted">
            ヤマト・佐川のCSVをアップロードします。文字コード・キャリア・対象期間を自動判定します。
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <button className="btn" onClick={() => onNav("home")}><Ic.chevronL size={13} /> ホーム</button>
        <button
          className="btn primary"
          disabled={matchedCount === 0}
          onClick={() => onNav("list")}
        >
          支払明細 一覧へ <Ic.arrowR size={14} />
        </button>
      </div>

      {error && (
        <div className="toast warn">
          <Ic.alert size={14} />
          <div><b>取込エラー</b><div className="tiny">{error}</div></div>
        </div>
      )}

      {blocked && <UnknownBlockBanner report={blocked} onDismiss={() => setBlocked(null)} />}

      {monthLocked && (
        <div className="toast info">
          <Ic.checkCircle size={14} />
          <div className="col gap-4">
            <div><b>{state.targetMonth} は確定済みです</b></div>
            <div className="tiny">
              同じ月のCSVの取り込み直し・削除はできません。次の月のCSVをアップロードすると、新しい月の作業が始まります。
              この月を直す場合は「支払明細 一覧」で「この月を訂正する」を押してください。
            </div>
          </div>
        </div>
      )}

      {periodMismatch && (
        <div className="toast err">
          <Ic.alert size={14} />
          <div className="col gap-4">
            <div><b>取込中のCSVの対象月が揃っていません</b></div>
            <div className="tiny">
              ヤマト：{formatMonthLabel(periodMismatch.yamatoMonth)} ／ 佐川：{formatMonthLabel(periodMismatch.sagawaMonth)}
            </div>
            <div className="tiny">
              このままだと2か月分が混ざった金額になります。前月分のCSVを下の「削除」で外してから、当月分をアップロードしてください。
            </div>
          </div>
        </div>
      )}

      {(yamatoZeroAlert || sagawaZeroAlert) && (
        <div className="toast warn">
          <Ic.alert size={14} />
          <div className="col gap-4">
            <div><b>売上が 0 円のキャリアがあります</b></div>
            {yamatoZeroAlert && (
              <div className="tiny">
                ヤマト：マスター一致 {yamatoStmts.length} 件あるが、売上合計が 0 円。
                CSV ファイルの稼働実績データを確認してください（修正前/修正後個数とも0の可能性）
              </div>
            )}
            {sagawaZeroAlert && (
              <div className="tiny">
                佐川：マスター一致 {sagawaStmts.length} 件あるが、売上合計が 0 円。
                単数単価が 183/184/40/350 以外の便種ばかりの可能性
              </div>
            )}
          </div>
        </div>
      )}

      <div className="csv-grid">
        <div className="csv-col">
          <CsvDrop
            carrier="yamato"
            imp={yamatoImp}
            onUpload={handleFile}
            onClear={() => clearImport("yamato")}
            busy={busy}
          />
          <CsvDrop
            carrier="sagawa"
            imp={sagawaImp}
            onUpload={handleFile}
            onClear={() => clearImport("sagawa")}
            busy={busy}
          />
        </div>

        <aside className="csv-side">
          <div className="card">
            <div className="card-head"><div className="card-title">取込サマリー</div></div>
            <div className="card-pad col gap-12">
              <SumRow k="対象月" v={<b>{state.targetMonth}</b>} />
              <SumRow k="取込行数 合計" v={`${totalRows.toLocaleString()} 行`} />
              <SumRow k="ドライバー数 (CSV内)" v={`${allCsvNames.size} 名`} />
              <SumRow k="マスター一致" v={<><b>{matchedCount}</b> 件の明細を生成</>} />
              {candidates > 0 && (
                <SumRow k="名寄せ候補" v={<span className="chip amber">{candidates} 名</span>} />
              )}
              {unmatched > 0 && (
                <SumRow k="未登録" v={<span className="chip red">{unmatched} 名</span>} />
              )}
              <SumRow
                k="月別控除入力"
                v={
                  state.sheetMaster ? (
                    monthlyDed.length > 0 ? (
                      <><b>{monthlyDed.length}</b> 件 <span className="tiny muted">（ガソリン {gasDedCount} 件）</span></>
                    ) : (
                      <span className="chip amber" title="マスターシートの「月別控除入力」タブに当月分の入力がありません。入力済みなのに0件の場合は、列見出しが「2026年6月」形式か・月が合っているか確認してください">
                        当月 0 件
                      </span>
                    )
                  ) : (
                    <span className="tiny muted">シート未接続</span>
                  )
                }
              />
              {state.sheetMaster && (
                <SumRow k="マスター取得" v={<span className="tiny">{new Date(state.sheetMaster.fetchedAt).toLocaleString("ja-JP")}</span>} />
              )}
              <hr className="hr" />
              <div className="muted tiny" style={{ fontWeight: 600 }}>キャリア別 売上集計</div>
              <SumRow
                k={<><span className="carrier-dot" style={{ background: "#0066b3" }} /> ヤマト</>}
                v={
                  yamatoImp ? (
                    yamatoSales > 0
                      ? <><b>{yen(yamatoSales)}</b> 円 <span className="tiny muted">／ {yamatoStmts.length} 名</span></>
                      : <span className="chip red">売上0 — 要確認</span>
                  ) : <span className="tiny muted">未取込</span>
                }
              />
              <SumRow
                k={<><span className="carrier-dot" style={{ background: "#0073c4" }} /> 佐川</>}
                v={
                  sagawaImp ? (
                    sagawaSales > 0
                      ? <><b>{yen(sagawaSales)}</b> 円 <span className="tiny muted">／ {sagawaStmts.length} 名</span></>
                      : <span className="chip red">売上0 — 要確認</span>
                  ) : <span className="tiny muted">未取込</span>
                }
              />
              <hr className="hr" />
              <div className="tiny muted">
                突合結果は次の「支払明細 一覧」で確認できます。未登録ドライバーはマスター追加後に再取込してください。
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-head"><div className="card-title">取込仕様</div></div>
            <div className="card-pad col gap-8 tiny muted">
              <div>・ヤマト運輸 / 佐川急便のCSVのみ受付（拡張子 .csv）</div>
              <div>・文字コードは Shift_JIS / UTF-8（BOM可）を自動判定</div>
              <div>・列マッピングは「キャリア × ヘッダー名」で自動判定</div>
              <div>・1ファイル数MB〜10MB程度を想定</div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

function SumRow({ k, v }: { k: React.ReactNode; v: React.ReactNode }) {
  return (
    <div className="sum-row">
      <div className="muted" style={{ display: "flex", alignItems: "center", gap: 6 }}>{k}</div>
      <div className="num">{v}</div>
    </div>
  );
}

function CsvDrop({
  carrier,
  imp,
  onUpload,
  onClear,
  busy,
}: {
  carrier: Carrier;
  imp: ReturnType<typeof useStore>["state"]["imports"][Carrier];
  onUpload: (file: File) => void;
  onClear: () => void;
  busy: boolean;
}) {
  const ref = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);

  if (!imp) {
    return (
      <div className="card">
        <div className="card-head">
          <div className="row gap-8" style={{ alignItems: "center" }}>
            <span className="carrier-dot" style={{ background: CARRIER_COLOR[carrier] }} />
            <div className="card-title">{CARRIER_LABEL[carrier]}</div>
            <span className="chip">必須</span>
          </div>
        </div>
        <div
          className={"drop-zone empty" + (drag ? " drag" : "")}
          onClick={() => ref.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            const f = e.dataTransfer.files[0];
            if (f) onUpload(f);
          }}
          style={{ cursor: "pointer" }}
        >
          <Ic.upload size={26} />
          <div style={{ fontSize: 13, fontWeight: 600, marginTop: 6 }}>
            {busy ? "読込中..." : "CSVファイルをドラッグ＆ドロップ"}
          </div>
          <div className="tiny muted" style={{ marginTop: 2 }}>
            または <span style={{ color: "var(--blue-2)" }}>ファイルを選択</span> ／ Shift_JIS / UTF-8 / 自動判定
          </div>
          <input
            ref={ref}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
            }}
          />
        </div>
      </div>
    );
  }

  const m = imp.meta;
  return (
    <div className="card">
      <div className="card-head">
        <div className="row gap-8" style={{ alignItems: "center" }}>
          <span className="carrier-dot" style={{ background: CARRIER_COLOR[carrier] }} />
          <div className="card-title">{CARRIER_LABEL[carrier]}</div>
          <span className="chip green dot">取込済</span>
        </div>
        <div className="row gap-6">
          <button className="btn sm ghost" onClick={() => ref.current?.click()}>再アップロード</button>
          <button className="btn sm ghost" onClick={onClear}>削除</button>
          <input
            ref={ref}
            type="file"
            accept=".csv"
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onUpload(f);
            }}
          />
        </div>
      </div>
      <div className="card-pad">
        <div className="file-line">
          <Ic.doc size={16} />
          <div className="col" style={{ flex: 1 }}>
            <div style={{ fontWeight: 600 }}>{m.fileName}</div>
            <div className="tiny muted">
              {(m.sizeBytes / 1024).toFixed(1)} KB ・ 取込: {new Date(m.uploadedAt).toLocaleString("ja-JP")}
            </div>
          </div>
        </div>

        <div className="meta-grid">
          <Meta k="行数" v={`${m.rows.toLocaleString()} 行`} />
          <Meta
            k="対象期間"
            v={m.periodStart && m.periodEnd ? `${m.periodStart} 〜 ${m.periodEnd}` : "—"}
          />
          <Meta
            k="キャリア判定"
            v={
              <span className="row gap-6" style={{ alignItems: "center" }}>
                <Ic.checkCircle size={13} style={{ color: "var(--green)" }} />
                {CARRIER_LABEL[m.carrier]}
              </span>
            }
          />
          <Meta k="文字コード" v={m.encoding} />
        </div>
      </div>
    </div>
  );
}

function Meta({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="meta-cell">
      <div className="meta-k">{k}</div>
      <div className="meta-v">{v}</div>
    </div>
  );
}

// Issue #7：未知の便種/列を検出して取込をブロックした際の赤バナー
function UnknownBlockBanner({ report, onDismiss }: { report: UnknownReport; onDismiss: () => void }) {
  const isSagawa = report.carrier === "sagawa";
  return (
    <div
      className="toast"
      style={{
        background: "#fdecec",
        border: "1px solid #f5b5b5",
        color: "#7a1f1f",
        alignItems: "flex-start",
      }}
    >
      <Ic.alert size={16} style={{ color: "#c0392b", flexShrink: 0, marginTop: 2 }} />
      <div className="col gap-8" style={{ flex: 1 }}>
        <div>
          <b>未知の{isSagawa ? "便種" : "数量列"}を検出したため、このファイルの取込を保留しました</b>
          <div className="tiny" style={{ marginTop: 2 }}>
            ファイル：{report.fileName}
          </div>
        </div>
        <div className="tiny">
          想定外の項目を黙って計上漏れさせないため、ファイル全体を取り込んでいません（計上漏れ＝未払い防止）。
          下記の計上方法をICB様（本城さん）に確認し、計上ルールを反映してから再取込してください。
        </div>
        {isSagawa && (
          <div className="tiny" style={{ background: "#fff", border: "1px solid #f0c9c9", borderRadius: 6, padding: "6px 10px" }}>
            👉 <b>設定 → 佐川 便種マッピング</b> で、下表の<b>便種コード</b>を計上区分（宅急便単価／ネコポス単価／その他／対象外）に割り当てると、この便種が取り込めるようになります。
          </div>
        )}

        {isSagawa && report.sagawa && (
          <table className="tiny" style={{ borderCollapse: "collapse", width: "100%", background: "#fff" }}>
            <thead>
              <tr style={{ textAlign: "left", background: "#fae3e3" }}>
                <th style={cellStyle}>便種ｺｰﾄﾞ</th>
                <th style={cellStyle}>実績便種名</th>
                <th style={cellStyle}>単価</th>
                <th style={{ ...cellStyle, textAlign: "right" }}>行数</th>
                <th style={{ ...cellStyle, textAlign: "right" }}>個数合計</th>
                <th style={cellStyle}>出現日(例)</th>
              </tr>
            </thead>
            <tbody>
              {report.sagawa.map((u) => (
                <tr key={u.benkindCode}>
                  <td style={{ ...cellStyle, fontWeight: 700 }}>{u.benkindCode || "—"}</td>
                  <td style={cellStyle}>{u.benkindNames.join("・") || "—"}</td>
                  <td style={cellStyle}>{u.tans.join(" / ") || "—"}</td>
                  <td style={{ ...cellStyle, textAlign: "right" }}>{u.rowCount}</td>
                  <td style={{ ...cellStyle, textAlign: "right" }}>{u.totalQty.toLocaleString()}</td>
                  <td style={cellStyle}>{u.sampleDates.join(" / ") || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {!isSagawa && report.yamato && (
          <ul className="tiny" style={{ margin: "0 0 0 16px" }}>
            {report.yamato.map((u) => (
              <li key={u.column}>
                未知の数量列：<b>{u.column}</b>
              </li>
            ))}
          </ul>
        )}

        <div className="row gap-8">
          <button className="btn sm ghost" onClick={onDismiss}>閉じる</button>
        </div>
      </div>
    </div>
  );
}

const cellStyle: React.CSSProperties = {
  border: "1px solid #f0c9c9",
  padding: "4px 8px",
  whiteSpace: "nowrap",
};
